/**
 * TipTap rich text editor with KaTeX inline math rendering.
 * Supports bold/italic/code/link + $...$ LaTeX rendering via katex.
 */
import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import katex from "katex";
import "katex/dist/katex.min.css";
import { TextB, TextItalic, Code, Link as LinkIcon, ListBullets, ListNumbers, Quotes } from "@phosphor-icons/react";

export default function RichTextEditor({ value, onChange, placeholder = "Start writing…", testId = "rich-text-editor" }) {
  const editor = useEditor({
    extensions: [StarterKit, Link.configure({ openOnClick: false })],
    content: value || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  if (!editor) return null;

  const insertMath = () => {
    const formula = prompt("Enter LaTeX (e.g. \\frac{a}{b})");
    if (!formula) return;
    try {
      const html = katex.renderToString(formula, { throwOnError: false });
      editor.chain().focus().insertContent(`<span class="katex-rendered">${html}</span>`).run();
    } catch (_e) {
      // ignore
    }
  };

  const Btn = ({ active, onClick, children, label }) => (
    <button
      type="button"
      onClick={onClick}
      title={label}
      className={`p-2 border border-zinc-200 ${active ? "bg-zinc-900 text-white border-zinc-900" : "bg-white hover:bg-zinc-50"}`}
    >
      {children}
    </button>
  );

  return (
    <div className="border border-zinc-300" data-testid={testId}>
      <div className="flex flex-wrap gap-1 p-1 border-b border-zinc-200 bg-zinc-50">
        <Btn active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} label="Bold"><TextB size={14} /></Btn>
        <Btn active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} label="Italic"><TextItalic size={14} /></Btn>
        <Btn active={editor.isActive("code")} onClick={() => editor.chain().focus().toggleCode().run()} label="Code"><Code size={14} /></Btn>
        <Btn active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} label="Bullets"><ListBullets size={14} /></Btn>
        <Btn active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} label="Ordered"><ListNumbers size={14} /></Btn>
        <Btn active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()} label="Quote"><Quotes size={14} /></Btn>
        <Btn onClick={() => {
          const url = prompt("URL");
          if (url) editor.chain().focus().setLink({ href: url }).run();
        }} label="Link"><LinkIcon size={14} /></Btn>
        <Btn onClick={insertMath} label="Insert LaTeX">
          <span className="font-serif italic text-xs px-0.5">∑x</span>
        </Btn>
      </div>
      <EditorContent
        editor={editor}
        className="p-3 max-w-none min-h-[120px] focus:outline-none [&_p]:my-1 [&_strong]:font-semibold [&_em]:italic [&_code]:bg-zinc-100 [&_code]:px-1 [&_code]:font-mono [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_blockquote]:border-l-4 [&_blockquote]:border-zinc-300 [&_blockquote]:pl-4 [&_.katex-rendered]:inline-block [&_.ProseMirror]:focus:outline-none [&_.ProseMirror]:min-h-[100px]"
        placeholder={placeholder}
      />
    </div>
  );
}
